import React, { useState, useEffect, useContext } from 'react';
import { RadioGroupField, InputField } from '@trp-beacon-ui-kit/react-controls';
import styles from './RPSOCRChooseMyFunds.css';
import {
  shouldValidate,
  disableEditBtn,
  disableRolloverAmtInput,
  checkExistingAgreements,
  defaultRolloverAmt
} from './utils';
import { CartContext } from '../../context/CartContext/CartContext';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import { iClassFunds, removeHiddenFunds } from '../FundListFragment/util';
import FONASUAllocationTable from '../FONASUAllocationTable/FONASUAllocationTable';
import FundQuickSearch from '../FundQuickSearch/FundQuickSearch';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import useCartDetailsState from '../../hooks/useCartDetailsState';
import { FlowContext } from '../../core/flow-engine';
import {
  isRPSOCRTraditionalAccount,
  isRPSOCROmniAccount,
  ocrRolloverAmountOptions
} from '../../flows/front-office/shared/utils';

export const RenderRPSOCRExistingAgreements = ({
  updatedExistingAgreements,
  existingRPSAgreements
}) => {
  return (
    <>
      {updatedExistingAgreements?.length > 0 && (
        <>
          <div className="fund-option-heading">Existing Rollover IRA(s)</div>
          {updatedExistingAgreements.map(agreement => {
            return (
              <div
                className="fund-row"
                key={`${agreement.ticker}-${agreement?.agreementKey?.agreementNumber}`}>
                <div className="fund-name">
                  {agreement.agreementType}
                  <span className="fund-name">- {agreement.fundName}</span>
                  <span className="ticker">{agreement.ticker}</span>
                </div>
                <div>
                  <span className="agreement-number">
                    {agreement?.agreementKey?.agreementNumber} -{' '}
                    {agreement?.agreementKey?.checkSum}
                  </span>
                </div>
              </div>
            );
          })}
        </>
      )}
      {existingRPSAgreements?.length > 0 && (
        <>
          <div className="fund-option-heading">Existing RPS funds</div>
          {existingRPSAgreements.map(agreement => {
            return (
              <div className="fund-row" key={agreement.ticker}>
                <div className="fund-name">
                  <span className="fund-name">{agreement.name}</span>
                  <span className="ticker">{agreement.ticker}</span>
                </div>
              </div>
            );
          })}
        </>
      )}

      <style jsx>{styles}</style>
    </>
  );
};

const RPSOCRChooseFundsReview = ({
  updatedExistingAgreements,
  existingRPSAgreements,
  fundsWithCategories,
  editPortfolio,
  maxRolloverAmt,
  rolloverAmt,
  rolloverAmountOption,
  matchingAgreementArray,
  rewardsAvailable
}) => {
  return (
    <>
      <div className="sub-heading">Allocate Your Investments</div>
      <div className="field-label">
        <div>Maximum Rollover Amount: $ {maxRolloverAmt}</div>
      </div>
      {rolloverAmountOption !== '' ? (
        <>
          <div className="field-label">
            <div>Estimated Rollover Amount: $ {rolloverAmt}</div>
          </div>
          <div className="field-label">
            <div>Rollover Option Chosen: {rolloverAmountOption}</div>
          </div>
          {Array.isArray(updatedExistingAgreements) &&
            updatedExistingAgreements.length >= 1 && (
              <RenderRPSOCRExistingAgreements
                updatedExistingAgreements={updatedExistingAgreements}
                existingRPSAgreements={existingRPSAgreements}
              />
            )}
          <div className="fund-option-heading">Fund(s) Selected</div>
          <FONASUAllocationTable
            fundsWithCategories={fundsWithCategories}
            readOnly={!editPortfolio}
            matchingAgreementArray={matchingAgreementArray}
            rewardsAvailable={rewardsAvailable}
          />
        </>
      ) : (
        <>
          <div className="sub-heading">Fund(s) Selected</div>
          <div className="no-funds">You have not made any selections yet.</div>
        </>
      )}

      <style jsx>{styles}</style>
    </>
  );
};

/* eslint-disable */

const RPSOCRChooseMyFunds = ({
  editPortfolio,
  setEditPortfolio,
  investmentAmt: rolloverAmt,
  setInvestmentAmt: setRolloverAmt,
  allMutualFunds,
  next,
  error
}) => {
  const {
    totalAmount,
    selectedFunds,
    isValidTotal,
    data = {},
    api: { addFund, fundAdded, changeTotalAmount }
  } = useContext(CartContext);

  const {
    appState: {
      store: {
        loadedData: {
          portfolioValid,
          maxRolloverAmt,
          rolloverAmountOption,
          existingAgreements,
          existingRPSAgreements,
          updatedExistingAgreements,
          termStatus,
          inOCRAltFlow,
          previousRolloverAmt,
          portfolioEditMode
        },
        accountType
      }
    },
    formProps
  } = useContext(FlowContext);

  const { rewardsAvailable } = useContext(RewardsContext);

  const { iClassFunds: iClassFundsReward } = rewardsAvailable;

  const isEstimatedRolloverAmtZero = maxRolloverAmt === '0';
  const [searchableFunds, setSearchableFunds] = useState(allMutualFunds);
  const [rolloverAmount, setRolloverAmount] = useState(rolloverAmt);
  const [exceedsPercentage, setExceedsPercentage] = useState(false);
  const [exceedsMaxBalance, setExceedsMaxBalance] = useState(false);
  const [estimatedAmtOption, setEstimatedAmtOption] = useState(
    isEstimatedRolloverAmtZero
      ? ocrRolloverAmountOptions.DOLLAR
      : rolloverAmountOption
  );

  useEffect(() => {
    setEditPortfolio(portfolioEditMode);
  }, [portfolioEditMode]);

  useEffect(() => {
    setSearchableFunds(
      removeHiddenFunds(
        iClassFunds(totalAmount, allMutualFunds, iClassFundsReward)
      )
    );
  }, [totalAmount, allMutualFunds]);

  const { mutualFunds: mutualFundsWithAllocations = [] } = data;

  const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
  const accountText = isOCRTraditionalAcc ? 'Traditional' : 'Roth';
  const isRPSOMNI = isRPSOCROmniAccount(accountType);

  const ocrChooseFundsText = `[Please make a selection below]. If an existing T. Rowe Price
  ${accountText} or ${accountText} Rollover IRA was selected, it will display below. Retirement
  plan assets can be rolled into one of these existing fund selections and/or new T. Rowe Price fund(s).
  New T. Rowe Price funds can only be added for ${accountText} IRA if an existing ${accountText} IRA fund was selected as well.`;

  const existingAgreementsText = `The fund selected is an existing IRA. Please go back to the Existing
  IRA Accounts section and select the desired fund to add to the cart, and then click Done. Then return 
  to this section to review and ensure the allocations are correct, before clicking Done.`;

  const { fundsWithCategories, validCart } = useCartDetailsState({
    selectedFunds,
    mutualFundsWithAllocations,
    rewardsAvailable,
    totalAmount
  });

  // Get Tickers from Selected Funds
  const selectedFundsTickers = selectedFunds?.map(({ ticker }) => ticker);

  //Get matching Tickers Array from updated existing agrrements and funds in cart
  const matchingAgreementArray = checkExistingAgreements(
    updatedExistingAgreements,
    fundsWithCategories
  );

  //Get matching Tickers Array from existing agrrements and funds in cart
  const tickersInExistingAgreements = checkExistingAgreements(
    existingAgreements,
    fundsWithCategories
  );

  // check if selected fund tickers are present in existing agreements but user has chosen
  // not to select it from existing funds section
  const isExistingAgreementNotchosen = selectedFundsTickers?.some(
    ticker =>
      !matchingAgreementArray.includes(ticker) &&
      tickersInExistingAgreements.includes(ticker)
  );

  const isValid = shouldValidate(
    selectedFunds,
    editPortfolio,
    validCart,
    isValidTotal,
    portfolioValid,
    rolloverAmt,
    exceedsMaxBalance,
    exceedsPercentage,
    isExistingAgreementNotchosen
  );

  const shouldDisableEditBtn = disableEditBtn(isValid, error);

  const shouldDisableRolloverAmtInput = disableRolloverAmtInput(
    estimatedAmtOption
  );

  const displayDefaultRolloverAmt = defaultRolloverAmt(
    rolloverAmount,
    shouldDisableRolloverAmtInput
  );

  const handleClick = async () => {
    if (isValid) {
      if (editPortfolio) {
        await next({
          section: 'ocrChooseFunds',
          funds: fundsWithCategories,
          rolloverAmt,
          rolloverAmountOption: estimatedAmtOption,
          matchingAgreementArray
        })();
      } else {
        await next('unlockRPSChooseFundsSection')();
      }
    }
  };

  const addSelectedFund = fundText => {
    if (fundText.length > 0) {
      let selectedFund = [...searchableFunds];

      selectedFund = selectedFund.filter(
        ({ name, ticker }) => fundText === `${name} (${ticker})`
      );

      if (selectedFund.length === 1 && !fundAdded(selectedFund[0].ticker)) {
        addFund({
          name: selectedFund[0].name,
          ticker: selectedFund[0].ticker,
          amount: 0,
          percent: 0
        });
      }
    }
  };

  const calculateFundPercentage = (amt, val) => {
    return Math.round(parseFloat(amt * (val / 100)));
  };

  const handleEstimatedRolloverAmtType = event => {
    setEstimatedAmtOption(event.target.value);
    if (event.target.value === ocrRolloverAmountOptions.PERCENTAGE) {
      if (rolloverAmount > 100) {
        setExceedsPercentage(true);
      }
      setExceedsMaxBalance(false);
      setRolloverAmt(calculateFundPercentage(maxRolloverAmt, rolloverAmount));
      changeTotalAmount(
        calculateFundPercentage(maxRolloverAmt, rolloverAmount)
      );
    } else if (event.target.value === ocrRolloverAmountOptions.DOLLAR) {
      setRolloverAmt(rolloverAmount);
      changeTotalAmount(rolloverAmount);
      setExceedsPercentage(false);
      if (isRPSOMNI && termStatus && rolloverAmount > maxRolloverAmt) {
        setExceedsMaxBalance(true);
      }
    } else {
      setRolloverAmt(maxRolloverAmt);
      changeTotalAmount(maxRolloverAmt);
      setExceedsPercentage(false);
      setExceedsMaxBalance(false);
    }
  };

  const handleCurrentRolloverVal = ({ target: { value } }) => {
    const val = Number(value);
    setRolloverAmount(val);
    setExceedsPercentage(false);
    if (estimatedAmtOption === ocrRolloverAmountOptions.PERCENTAGE) {
      if (val > 100) {
        setExceedsPercentage(true);
      }
      setRolloverAmt(calculateFundPercentage(maxRolloverAmt, val));
      changeTotalAmount(calculateFundPercentage(maxRolloverAmt, val));
    } else if (estimatedAmtOption === ocrRolloverAmountOptions.DOLLAR) {
      setExceedsMaxBalance(false);
      setRolloverAmt(val);
      changeTotalAmount(val);
      if (isRPSOMNI && termStatus && val > maxRolloverAmt) {
        setExceedsMaxBalance(true);
      }
    } else {
      setRolloverAmt(maxRolloverAmt);
    }
  };

  const estimatedRolloverAmtOptions = [
    {
      title: '% (in percent)',
      value: ocrRolloverAmountOptions.PERCENTAGE,
      defaultChecked:
        rolloverAmountOption === ocrRolloverAmountOptions.PERCENTAGE,
      disabled: isEstimatedRolloverAmtZero
    },
    {
      title: '$ (in dollars)',
      value: ocrRolloverAmountOptions.DOLLAR,
      defaultChecked:
        rolloverAmountOption === ocrRolloverAmountOptions.DOLLAR ||
        isEstimatedRolloverAmtZero
    },
    {
      title: 'All',
      value: ocrRolloverAmountOptions.ALL,
      defaultChecked: rolloverAmountOption === ocrRolloverAmountOptions.ALL,
      disabled: isEstimatedRolloverAmtZero
    }
  ];

  const onKeyDown = event => {
    if (['.', '-'].includes(event.key)) event.preventDefault();
  };

  const previousRolloverAmtInfo = inOCRAltFlow
    ? `$${previousRolloverAmt} was used in the previous flow.`
    : '';
  const maxRolloverAmtInfo = `The max rollover amount includes both pre and after tax money. 
  Please use the record keeper to view specific amounts for each money source. ${previousRolloverAmtInfo}`;

  return (
    <div className="review-portfolio">
      <div className="section-header">
        <SectionHeading text="Choose Your Funds"></SectionHeading>
      </div>
      <div className="section-content">
        {editPortfolio && (
          <>
            <div className="sub-heading">Allocate Your Investments</div>
            <div className="field-label">
              <div>Maximum Rollover Amount: $ {maxRolloverAmt}</div>
              <div>{maxRolloverAmtInfo}</div>
            </div>
            <div className="qv-row">
              <div className="qv-col">
                <InputField
                  type="number"
                  maxLength="12"
                  title="Approximate Rollover Amount:"
                  onKeyDown={onKeyDown}
                  onChange={handleCurrentRolloverVal}
                  disabled={shouldDisableRolloverAmtInput}
                  value={displayDefaultRolloverAmt}
                />
                {exceedsPercentage && (
                  <div className="error-message">
                    * Please enter a percent amount that is less than or equal
                    to 100%
                  </div>
                )}
                {exceedsMaxBalance && (
                  <div className="error-message">
                    * Please enter a dollar amount that is less than maximum
                    avaliable amount.
                  </div>
                )}
              </div>
              <div className="qv-col">
                <div className="account-option-input">
                  <RadioGroupField
                    options={estimatedRolloverAmtOptions}
                    onChange={handleEstimatedRolloverAmtType}
                    vertical
                    {...formProps.rolloverAmountOption}
                    name="rolloverAmountOptions"
                  />
                </div>
              </div>
            </div>
            <div className="field-label">
              {`Estimated Rollover Amount: $ ${
                !exceedsPercentage && estimatedAmtOption !== undefined
                  ? rolloverAmt
                  : ''
              }`}
              {rolloverAmt >= 1000 || (
                <div className="error-message">
                  * Minimum investment amount should be greater than or equal to
                  $1000
                </div>
              )}
            </div>

            <div className="status-text">
              <ul>
                <li>
                  Please note that amounts are approximate due to market price
                  fluctuations.
                </li>
                {termStatus || (
                  <li className="term-status-text">
                    This participant is NOT in 'TERM' status. Verify that the
                    correct 'Maximum Rollover Amount' is displayed by reviewing
                    the participant's plan assets in Synergy and/or Smart Desk.
                    Enter the verified rollover amount available in the
                    'Approximate Rollover Amount' field to override the 'Maximum
                    Rollover Amount', IF the correct amount is not displayed.
                  </li>
                )}
              </ul>
            </div>

            <div className="fund-option-heading">Fund Options</div>
            <div className="fund-option-text">
              <div>{ocrChooseFundsText}</div>
            </div>

            {isExistingAgreementNotchosen && (
              <div className="error-message">
                <div>{existingAgreementsText}</div>
              </div>
            )}

            {existingRPSAgreements?.length > 0 && (
              <RenderRPSOCRExistingAgreements
                existingRPSAgreements={existingRPSAgreements}
              />
            )}

            <div className="qv-row">
              <div className="qv-col">
                <FundQuickSearch
                  updateFilterText={addSelectedFund}
                  allMutualFunds={searchableFunds}
                  mode="FONASU-portfolio"
                  placeholder="Search for Mutual Funds"
                />
              </div>
            </div>

            <FONASUAllocationTable
              fundsWithCategories={fundsWithCategories}
              readOnly={!editPortfolio}
              matchingAgreementArray={matchingAgreementArray}
              rewardsAvailable={rewardsAvailable}
              accountType={accountType}
            />
          </>
        )}

        {!editPortfolio && (
          <RPSOCRChooseFundsReview
            updatedExistingAgreements={updatedExistingAgreements}
            existingRPSAgreements={existingRPSAgreements}
            fundsWithCategories={fundsWithCategories}
            editPortfolio={editPortfolio}
            maxRolloverAmt={maxRolloverAmt}
            rolloverAmt={rolloverAmt}
            rolloverAmountOption={rolloverAmountOption}
            matchingAgreementArray={matchingAgreementArray}
            rewardsAvailable={rewardsAvailable}
          />
        )}
      </div>
      <EditButton
        onClick={handleClick}
        currentlyEditing={editPortfolio}
        disabled={shouldDisableEditBtn}
      />
      <style jsx>{styles}</style>
    </div>
  );
};
/* eslint-enable */

export default RPSOCRChooseMyFunds;
